"use strict";
// Utility functions for calculations and data processing
Object.defineProperty(exports, "__esModule", { value: true });
exports.FormatUtils = exports.PriceAnalyzer = exports.SetProgressCalculator = exports.LiquidityAnalyzer = exports.FinancialCalculator = void 0;
class FinancialCalculator {
    /**
     * Calculate realized gains from sold cards
     */
    static calculateRealizedGains(transactions) {
        const sales = transactions.filter((t) => t.type === 'SALE');
        return sales.reduce((total, sale) => {
            return total + (sale.netAmount || sale.totalAmount);
        }, 0);
    }
    /**
     * Calculate unrealized gains based on current market value vs cost basis
     */
    static calculateUnrealizedGains(cardInstances) {
        return cardInstances.reduce((total, card) => {
            const marketValue = card.currentMarketValue || 0;
            const costBasis = card.costBasis.totalCost;
            return total + (marketValue - costBasis);
        }, 0);
    }
    /**
     * Calculate ROI percentage
     */
    static calculateROI(gains, totalInvestment) {
        if (totalInvestment === 0)
            return 0;
        return (gains / totalInvestment) * 100;
    }
    /**
     * Distribute lot cost across multiple cards
     */
    static distributeLotCost(cardValues, totalCost, method) {
        if (method === 'EQUAL') {
            const costPerCard = totalCost / cardValues.length;
            return cardValues.map(() => costPerCard);
        }
        // VALUE_WEIGHTED
        const totalValue = cardValues.reduce((sum, value) => sum + value, 0);
        if (totalValue === 0) {
            const costPerCard = totalCost / cardValues.length;
            return cardValues.map(() => costPerCard);
        }
        return cardValues.map((value) => (value / totalValue) * totalCost);
    }
    /**
     * Calculate grading cost impact
     */
    static calculateGradingImpact(rawValue, gradedValue, gradingCost) {
        const valueIncrease = gradedValue - rawValue;
        const netGain = valueIncrease - gradingCost;
        const roi = gradingCost > 0 ? (netGain / gradingCost) * 100 : 0;
        return {
            valueIncrease,
            netGain,
            roi,
        };
    }
}
exports.FinancialCalculator = FinancialCalculator;
class LiquidityAnalyzer {
    /**
     * Calculate liquidity score based on sales data
     */
    static calculateLiquidityScore(salesVolume30Days, averageDaysToSell, priceStability = 0.8 // Default stability factor
    ) {
        // Base score from volume (0-40 points)
        const volumeScore = Math.min(salesVolume30Days * 2, 40);
        // Speed score from days to sell (0-40 points)
        const speedScore = Math.max(0, 40 - averageDaysToSell);
        // Stability score (0-20 points)
        const stabilityScore = priceStability * 20;
        return Math.min(100, volumeScore + speedScore + stabilityScore);
    }
    /**
     * Categorize liquidity level
     */
    static categorizeLiquidity(score) {
        if (score >= 80)
            return 'HIGH';
        if (score >= 60)
            return 'MEDIUM';
        if (score >= 30)
            return 'LOW';
        return 'VERY_LOW';
    }
}
exports.LiquidityAnalyzer = LiquidityAnalyzer;
class SetProgressCalculator {
    /**
     * Calculate set completion percentage
     */
    static calculateCompletion(totalCards, ownedCards) {
        if (totalCards === 0)
            return 0;
        return (ownedCards / totalCards) * 100;
    }
    /**
     * Find missing cards in a set
     */
    static findMissingCards(allCardIds, ownedCardIds) {
        return allCardIds.filter((cardId) => !ownedCardIds.includes(cardId));
    }
    /**
     * Calculate variant completion
     */
    static calculateVariantCompletion(cardId, variants) {
        const availableVariants = ['normal', 'reverseHolo', 'holo'];
        const ownedCount = availableVariants.filter((variant) => variants[variant]).length;
        return (ownedCount / availableVariants.length) * 100;
    }
}
exports.SetProgressCalculator = SetProgressCalculator;
class PriceAnalyzer {
    /**
     * Calculate price trend
     */
    static calculateTrend(currentPrice, previousPrice) {
        if (previousPrice === 0) {
            return { percentage: 0, direction: 'STABLE' };
        }
        const percentage = ((currentPrice - previousPrice) / previousPrice) * 100;
        let direction = 'STABLE';
        if (Math.abs(percentage) > 5) {
            // 5% threshold for significant change
            direction = percentage > 0 ? 'UP' : 'DOWN';
        }
        return { percentage, direction };
    }
    /**
     * Find best pricing source
     */
    static findBestPrice(prices) {
        if (prices.length === 0)
            return null;
        // Weight price by liquidity score
        const weightedPrices = prices.map((p) => ({
            ...p,
            weightedPrice: p.price * (p.liquidity.liquidityScore / 100),
        }));
        const best = weightedPrices.reduce((prev, current) => current.weightedPrice > prev.weightedPrice ? current : prev);
        return { bestPrice: best.price, source: best.source };
    }
}
exports.PriceAnalyzer = PriceAnalyzer;
// Format utilities
class FormatUtils {
    /**
     * Format currency value
     */
    static formatCurrency(amount, currency = 'USD') {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency,
        }).format(amount);
    }
    /**
     * Format percentage
     */
    static formatPercentage(value, decimals = 2) {
        return `${value.toFixed(decimals)}%`;
    }
    /**
     * Format date
     */
    static formatDate(dateString, locale = 'en-US') {
        return new Date(dateString).toLocaleDateString(locale);
    }
    /**
     * Format card number for display
     */
    static formatCardNumber(number, totalCards) {
        const paddedNumber = number.padStart(3, '0');
        return `${paddedNumber}/${totalCards.toString().padStart(3, '0')}`;
    }
}
exports.FormatUtils = FormatUtils;
